import React from 'react';

// Monochrome inline SVG wordmarks — rendered in currentColor so they inherit
// text color and stay on-brand. Designed to feel like a unified set, not a
// random collection of brand assets.
const Mark = ({ children }: { children: React.ReactNode; key?: React.Key }) => (
  <span className="flex items-center gap-2.5 text-zinc-500 hover:text-white transition-colors duration-300 text-[17px] font-semibold tracking-[-0.02em] shrink-0 whitespace-nowrap">
    {children}
  </span>
);

const Dot = () => (
  <span className="w-1 h-1 rounded-full bg-zinc-700 shrink-0" aria-hidden="true" />
);

const items = [
  <Mark key="openai">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22.28 9.82a5.98 5.98 0 00-.52-4.91 6.05 6.05 0 00-6.51-2.91A6.02 6.02 0 005.15 2.9a5.99 5.99 0 00-4.01 2.9 6.06 6.06 0 00.74 7.11 5.98 5.98 0 00.52 4.91 6.05 6.05 0 006.51 2.91 5.97 5.97 0 004.5 2A6.05 6.05 0 0019.27 18a5.99 5.99 0 004-2.9 6.06 6.06 0 00-.99-5.28zm-9.06 12.67a4.48 4.48 0 01-2.88-1.04l.14-.08 4.78-2.76a.78.78 0 00.39-.68v-6.74l2.02 1.17v5.58a4.5 4.5 0 01-4.45 4.55zM3.64 17.4a4.48 4.48 0 01-.54-3.02l.14.08 4.78 2.76a.78.78 0 00.79 0l5.83-3.37v2.33a.07.07 0 01-.03.06l-4.82 2.79a4.5 4.5 0 01-6.15-1.64zM2.38 6.87a4.48 4.48 0 012.35-1.97v5.68a.78.78 0 00.39.68l5.83 3.37-2.02 1.17-4.82-2.79a4.5 4.5 0 01-1.73-6.14zM17.69 10.46L11.85 7.09l2.02-1.17 4.82 2.79a4.5 4.5 0 01-.69 8.12v-5.68a.78.78 0 00-.31-.69zM19.7 7.45l-.14-.09-4.77-2.78a.78.78 0 00-.79 0l-5.83 3.37V5.62a.07.07 0 01.03-.06l4.82-2.78a4.5 4.5 0 016.68 4.67zM7.03 11.55l-2.02-1.16v-5.59a4.5 4.5 0 017.38-3.45l-.14.08-4.78 2.76a.78.78 0 00-.39.68zM8.13 9.18L10.73 7.68l2.6 1.5v3l-2.6 1.5-2.6-1.5z" />
    </svg>
    OpenAI
  </Mark>,
  <Mark key="anthropic">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M13.827 3.52h3.603L24 20h-3.603zM6.38 3.52h3.758L16.64 20h-3.73l-1.314-3.42H4.53L3.218 20H0zm1.793 4.78l-2.208 5.783h4.416z" />
    </svg>
    Anthropic
  </Mark>,
  <Mark key="n8n">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <circle cx="5" cy="12" r="2.5" />
      <circle cx="12" cy="7" r="2.5" />
      <circle cx="12" cy="17" r="2.5" />
      <circle cx="19" cy="12" r="2.5" />
      <path d="M7 12 L10 12 M14 12 L17 12 M12 9.5 L12 14.5" stroke="currentColor" strokeWidth="1.5" />
    </svg>
    n8n
  </Mark>,
  <Mark key="hubspot">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.164 7.93V5.084a2.196 2.196 0 001.267-1.978v-.067A2.196 2.196 0 0017.238.846h-.067a2.196 2.196 0 00-2.193 2.193v.067c0 .923.57 1.71 1.378 2.037V7.93a6.22 6.22 0 00-2.953 1.3L5.592 3.135a2.433 2.433 0 00.091-.633A2.503 2.503 0 103.18 5.006c.442 0 .854-.123 1.213-.32l7.684 5.973a6.23 6.23 0 00-.97 3.364c0 1.262.374 2.437 1.013 3.425L9.78 19.804a2.017 2.017 0 00-.58-.092 2.03 2.03 0 102.03 2.03c0-.201-.036-.395-.091-.58l2.31-2.31a6.258 6.258 0 10.715-10.923zm-1.004 9.382a3.193 3.193 0 110-6.386 3.193 3.193 0 010 6.386z" />
    </svg>
    HubSpot
  </Mark>,
  <Mark key="salesforce">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M10 4a4 4 0 00-4 4c0 .3 0 .5.1.8A3 3 0 004 11a3 3 0 001 2.3A2.5 2.5 0 006 18c.6 0 1.2-.2 1.7-.5.5 1 1.6 1.7 2.8 1.7 1 0 1.8-.4 2.4-1 .7.7 1.7 1.1 2.8 1.1 2 0 3.7-1.5 4-3.4.9-.4 1.6-1.3 1.6-2.3 0-1-.6-1.9-1.5-2.3.3-.5.5-1.1.5-1.8 0-2-1.6-3.5-3.5-3.5-.8 0-1.5.2-2.1.6-.6-1-1.8-1.7-3.1-1.7-1.1 0-2.1.4-2.7 1.1A4 4 0 0010 4z" />
    </svg>
    Salesforce
  </Mark>,
  <Mark key="slack">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="3" y="10" width="6" height="2" rx="1" />
      <rect x="15" y="12" width="6" height="2" rx="1" />
      <rect x="10" y="3" width="2" height="6" rx="1" />
      <rect x="12" y="15" width="2" height="6" rx="1" />
      <rect x="10" y="10" width="4" height="4" rx="1" />
    </svg>
    Slack
  </Mark>,
  <Mark key="stripe">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M13.5 4C9.5 4 7 6 7 9c0 5 8 4 8 6.5 0 .8-.7 1.3-2 1.3-1.9 0-4.2-1-5.5-1.9v4c1.3.8 3.5 1.3 5.5 1.3 4.2 0 6.5-2 6.5-5 0-5-8-4.2-8-6.3 0-.8.6-1.2 1.8-1.2 1.7 0 3.7.7 5 1.4V4.8C16.8 4.3 15.3 4 13.5 4z" />
    </svg>
    Stripe
  </Mark>,
  <Mark key="google">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M12 5c1.9 0 3.6.7 4.9 1.9l2.8-2.8C17.8 2.2 15.1 1 12 1 7.4 1 3.5 3.6 1.6 7.5l3.3 2.5C5.9 7.1 8.7 5 12 5zM23 12c0-.8-.1-1.5-.2-2.2H12v4.3h6.2c-.3 1.4-1 2.6-2.2 3.4l3.4 2.6c2-1.8 3.2-4.5 3.2-7.8zm-19.4 2l-3.3 2.5C2.2 20.4 6.8 23 12 23c3.1 0 5.7-1 7.6-2.8l-3.4-2.6c-1 .7-2.3 1.1-4 1.1-3.3 0-6.2-2.1-7.2-5.1zM12 23c-4.4 0-8.1-1.8-10.7-5l3.3-2.5c1 3 3.9 5.1 7.2 5.1 2.2 0 3.9-.6 5.3-1.7l3.4 2.6C17.7 22 15 23 12 23z" />
    </svg>
    Google
  </Mark>,
  <Mark key="notion">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M4.5 4L19.5 3l.5 1v16l-1.5 1L4.5 20V4zm2 2v13l11 .5V5.5L6.5 6zm2 2l2-.2v9l-2 .2V8zm3-.2l2.5 4v-3.8l-2.5.3v-.5zm0 4.5v4.5l2.5-.2V14l-2.5-1.7zm4-4.5l2.5-.3v8.8l-2.5.2V7.8z" />
    </svg>
    Notion
  </Mark>,
  <Mark key="make">
    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <rect x="4" y="6" width="3" height="12" rx="1" />
      <path d="M11 6c-.6 0-1 .4-1 1v10l3-4V7c0-.6-.4-1-1-1z" />
      <rect x="17" y="6" width="3" height="12" rx="1" transform="rotate(12 18.5 12)" />
    </svg>
    Make
  </Mark>,
];

const IntegrationsMarquee = () => {
  const reel = (
    <>
      {items.map((item, i) => (
        <React.Fragment key={i}>
          {item}
          <Dot />
        </React.Fragment>
      ))}
    </>
  );

  return (
    <section aria-label="Integrations" className="py-14 md:py-16 relative">
      <div className="container-custom mb-6 md:mb-8">
        <div className="flex items-center gap-3">
          <span className="eyebrow">Connects with your stack</span>
          <span className="flex-1 h-px bg-white/10" />
        </div>
      </div>

      <div className="marquee">
        <div className="marquee__track">
          {reel}
          {/* duplicate for seamless loop */}
          {reel}
        </div>
      </div>
    </section>
  );
};

export default IntegrationsMarquee;
